import type { OperationItem } from '../types'
import { toOperationRun, useRealtimeRunsWithTag } from '@foodlista/trigger-vue'
import { useQuery } from '@tanstack/vue-query'
import { operationIdOf } from '../completion/extract'
import { OPERATIONS_CACHE_KEYS, REALTIME_TOKEN_REFRESH_MS } from '../constants'
import {
  runsByVisibleOperationIds,
  visibleOperationIds,
} from '../utils/operation-feed'

import { hasWatchedOperations } from '../utils/watched-operations'

export function useRestaurantOperations(
  restaurantSlug: MaybeRefOrGetter<string | undefined>,
  options?: {
    watchedOperationIds?: MaybeRefOrGetter<ReadonlySet<string>>
  },
) {
  const { $orpc } = useNuxtApp()
  const slug = computed(() => toValue(restaurantSlug))
  const watchedOperationIds = computed(
    () => toValue(options?.watchedOperationIds) ?? new Set<string>(),
  )
  const enabled = computed(() => !!slug.value)

  const envelopes = useQuery({
    queryKey: OPERATIONS_CACHE_KEYS.envelopes(slug),
    queryFn: () => $orpc.operations.list({ restaurantSlug: slug.value! }),
    enabled,
  })

  const hasUndismissed = computed(() =>
    (envelopes.data.value ?? []).some(envelope => !envelope.dismissedAt))

  const shouldMaintainRealtime = computed(
    () => hasUndismissed.value || hasWatchedOperations(watchedOperationIds.value),
  )

  const tokenQuery = useQuery({
    queryKey: OPERATIONS_CACHE_KEYS.realtimeToken(slug),
    queryFn: () => $orpc.operations.realtimeToken({ restaurantSlug: slug.value! }),
    refetchInterval: REALTIME_TOKEN_REFRESH_MS,
    enabled: computed(() => enabled.value && shouldMaintainRealtime.value),
  })

  const realtimeAccessToken = computed(() => tokenQuery.data.value?.token)
  const realtimeTag = computed(() => tokenQuery.data.value?.tag)
  const realtimeTokenError = computed(() =>
    tokenQuery.isError.value
      ? (tokenQuery.error.value instanceof Error
          ? tokenQuery.error.value
          : new Error(String(tokenQuery.error.value)))
      : null,
  )
  const isRealtimeTokenPending = computed(
    () => tokenQuery.isPending.value || tokenQuery.isFetching.value,
  )

  const subscriptionEnabled = computed(
    () =>
      enabled.value
      && !!realtimeAccessToken.value
      && !!realtimeTag.value
      && shouldMaintainRealtime.value,
  )

  const { runs: liveRuns, error: streamErrorRef } = useRealtimeRunsWithTag(
    realtimeTag,
    {
      accessToken: realtimeAccessToken,
      enabled: subscriptionEnabled,
    },
  )

  const realtimeStreamError = computed(() => streamErrorRef.value)

  const allowedOperationIds = computed(() =>
    visibleOperationIds({
      envelopeIds: (envelopes.data.value ?? []).map(envelope => envelope.id),
      watchedIds: watchedOperationIds.value,
    }),
  )

  const liveByOperationId = computed(() =>
    runsByVisibleOperationIds(
      liveRuns.value.map(run => toOperationRun(run)),
      allowedOperationIds.value,
      operationIdOf,
    ),
  )

  const items = computed<OperationItem[]>(() =>
    (envelopes.data.value ?? [])
      .filter(envelope => !envelope.dismissedAt)
      .map(envelope => ({
        id: envelope.id,
        type: envelope.type,
        restaurantSlug: envelope.restaurantSlug,
        createdAt: new Date(envelope.createdAt),
        dismissedAt: envelope.dismissedAt ? new Date(envelope.dismissedAt) : null,
        contextVersion: envelope.contextVersion,
        context: envelope.context,
        run: liveByOperationId.value.get(envelope.id) ?? null,
      }))
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
  )

  return {
    items,
    liveByOperationId,
    envelopesFetched: computed(() => envelopes.isFetched.value),
    realtimeAccessToken,
    realtimeTag,
    realtimeTokenError,
    realtimeStreamError,
    isRealtimeTokenPending,
    invalidateEnvelopes: () =>
      envelopes.refetch(),
  }
}
